import { addDays, format, isSameDay, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";

export const WORK_DAY_START_HOUR = 8;
export const WORK_DAY_END_HOUR = 17;
export const SLOT_STEP_MINUTES = 30;
export const DEFAULT_MAX_SLOTS = 6;

export interface BusyInterval {
  start: Date;
  end: Date;
}

// Beräkna lediga starttider på 30-minutersgränser inom arbetsdagen (08–17).
// Slutet är halvöppet så att en lucka direkt efter ett möte räknas som ledig.
export const computeFreeSlots = (
  day: Date,
  durationMinutes: number,
  busy: BusyInterval[],
  now: Date = new Date()
): Date[] => {
  const dayStart = startOfDay(day);
  if (dayStart < startOfDay(now)) return [];

  // MeetingDuration.CUSTOM är 0 — skydda mot ogiltig längd
  const duration = durationMinutes > 0 ? durationMinutes : SLOT_STEP_MINUTES;

  const workStart = new Date(dayStart);
  workStart.setHours(WORK_DAY_START_HOUR, 0, 0, 0);
  const workEnd = new Date(dayStart);
  workEnd.setHours(WORK_DAY_END_HOUR, 0, 0, 0);

  let firstCandidate = workStart;
  if (isSameDay(day, now) && now > workStart) {
    // Runda upp till nästa halvtimme
    const rounded = new Date(now);
    rounded.setSeconds(0, 0);
    const minutes = rounded.getMinutes();
    const remainder = minutes % SLOT_STEP_MINUTES;
    if (remainder > 0 || now.getSeconds() > 0 || now.getMilliseconds() > 0) {
      rounded.setMinutes(minutes - remainder + SLOT_STEP_MINUTES);
    }
    firstCandidate = rounded;
  }

  const slots: Date[] = [];
  const stepMs = SLOT_STEP_MINUTES * 60 * 1000;
  const durationMs = duration * 60 * 1000;

  for (
    let time = firstCandidate.getTime();
    time + durationMs <= workEnd.getTime();
    time += stepMs
  ) {
    const slotStart = time;
    const slotEnd = time + durationMs;
    const overlapsBusy = busy.some(
      (interval) =>
        slotStart < interval.end.getTime() && slotEnd > interval.start.getTime()
    );
    if (!overlapsBusy) {
      slots.push(new Date(slotStart));
    }
  }

  return slots;
};

// Välj max `max` tider spridda över dagen (första + sista + jämnt däremellan)
// istället för de N första, som bara skulle ge förmiddagstider.
export const pickSpread = (slots: Date[], max: number): Date[] => {
  if (slots.length <= max) return slots;
  const indices = new Set<number>();
  for (let i = 0; i < max; i++) {
    indices.add(Math.round((i * (slots.length - 1)) / (max - 1)));
  }
  return Array.from(indices).map((index) => slots[index]);
};

export const formatDayLabel = (day: Date): string => {
  const today = new Date();
  if (isSameDay(day, today)) return "idag";
  if (isSameDay(day, addDays(today, 1))) return "imorgon";
  return format(day, "EEE d MMM", { locale: sv });
};
